import { supabase, supabaseConfigurado } from './cliente'
import type { Producto, Proveedor } from '../../domain/entidades'
import {
  demoListarProductos,
  demoListarProveedores,
  demoCompletarFicha,
  demoActualizarNombreProveedor,
  demoSuscribir,
} from '../demo/almacen-demo'

const CLAVE_CACHE_PRODUCTOS = 'feria-china-2026:cache-productos'
const CLAVE_CACHE_PROVEEDORES = 'feria-china-2026:cache-proveedores'

interface FilaProducto {
  id: string
  proveedor_codigo: string
  mueble: string
  categoria: string | null
  linea: string | null
  sublinea: string | null
  fob: number | null
  moq: number | null
  unidades_contenedor: number | null
  rango: 'Bajo' | 'Medio' | 'Alto' | null
  precio_venta_col: number | null
  precio_venta_pan: number | null
  nombre_comercial: string | null
  notas: string | null
  atributos_especiales: string[] | null
  fotos: { url: string; etiqueta: string }[] | null
  color_estructura: string | null
  color_estructura_foto_url: string | null
  telas: FilaTela[] | null
}

interface FilaTela {
  id: string
  producto_id: string
  muestrario: string | null
  colores: string | null
  tipo: string | null
  color_frente: string | null
  color_reverso: string | null
  confiabilidad_deteccion: number | null
  foto_frente_url: string | null
  foto_reverso_url: string | null
}

function mapearProducto(fila: FilaProducto): Producto {
  return {
    id: fila.id,
    proveedorCodigo: fila.proveedor_codigo,
    mueble: fila.mueble,
    categoria: fila.categoria ?? undefined,
    linea: fila.linea ?? undefined,
    sublinea: fila.sublinea ?? undefined,
    fob: fila.fob ?? undefined,
    moq: fila.moq ?? undefined,
    unidadesPorContenedor: fila.unidades_contenedor ?? undefined,
    rango: fila.rango ?? undefined,
    precioVentaCol: fila.precio_venta_col ?? undefined,
    precioVentaPan: fila.precio_venta_pan ?? undefined,
    nombreComercial: fila.nombre_comercial ?? '',
    notas: fila.notas ?? '',
    atributosEspeciales: (fila.atributos_especiales ?? []) as Producto['atributosEspeciales'],
    fotos: fila.fotos ?? undefined,
    colorEstructura: fila.color_estructura ?? undefined,
    colorEstructuraFotoUrl: fila.color_estructura_foto_url ?? undefined,
    telas: (fila.telas ?? []).map((t) => ({
      id: t.id,
      productoId: t.producto_id,
      muestrario: t.muestrario ?? '',
      colores: t.colores ?? '',
      tipo: (t.tipo ?? 'Otro') as Producto['telas'][number]['tipo'],
      colorFrente: t.color_frente ?? undefined,
      colorReverso: t.color_reverso ?? undefined,
      confiabilidadDeteccion: t.confiabilidad_deteccion ?? undefined,
      fotoFrenteUrl: t.foto_frente_url ?? undefined,
      fotoReversoUrl: t.foto_reverso_url ?? undefined,
    })),
  }
}

function leerCache<T>(clave: string): T[] | null {
  try {
    const crudo = localStorage.getItem(clave)
    if (!crudo) return null
    const datos = JSON.parse(crudo) as T[]
    return Array.isArray(datos) ? datos : null
  } catch {
    return null
  }
}

function guardarCache<T>(clave: string, datos: T[]): void {
  try {
    localStorage.setItem(clave, JSON.stringify(datos))
  } catch {
    // La caché local es best-effort.
  }
}

function pareceFallaDeRed(err: unknown): boolean {
  const mensaje =
    err instanceof Error
      ? err.message
      : err && typeof err === 'object' && 'message' in err
        ? String((err as { message?: unknown }).message ?? '')
        : String(err)
  return (
    !navigator.onLine ||
    /fetch|network|offline|timeout|timed out|failed to fetch|load failed/i.test(mensaje)
  )
}

const SELECT_PRODUCTO_CON_TELAS = '*, telas(*)'

export async function listarProductos(): Promise<Producto[]> {
  if (!supabaseConfigurado) return demoListarProductos()

  try {
    const { data, error } = await supabase
      .from('productos')
      .select(SELECT_PRODUCTO_CON_TELAS)
      .order('created_at', { ascending: false })

    if (error) throw error
    const productos = (data as unknown as FilaProducto[]).map(mapearProducto)
    guardarCache(CLAVE_CACHE_PRODUCTOS, productos)
    return productos
  } catch (err) {
    if (!pareceFallaDeRed(err)) throw err
    return leerCache<Producto>(CLAVE_CACHE_PRODUCTOS) ?? demoListarProductos()
  }
}

export async function listarProveedores(): Promise<Proveedor[]> {
  if (!supabaseConfigurado) return demoListarProveedores()

  try {
    const { data, error } = await supabase.from('proveedores').select('*')
    if (error) throw error
    const proveedores = (data ?? []).map((p) => ({
      codigo: p.codigo,
      nombre: p.nombre ?? '',
      contacto: p.contacto ?? undefined,
      telefono: p.telefono ?? undefined,
      ubicacion: p.ubicacion ?? undefined,
      notas: p.notas ?? undefined,
      fotoTarjetaUrl: p.foto_tarjeta_url ?? undefined,
    }))
    guardarCache(CLAVE_CACHE_PROVEEDORES, proveedores)
    return proveedores
  } catch (err) {
    if (!pareceFallaDeRed(err)) throw err
    return leerCache<Proveedor>(CLAVE_CACHE_PROVEEDORES) ?? demoListarProveedores()
  }
}

/** Editar el nombre de un proveedor desde el dashboard — pedido desde la feria, 2026-09-05. */
export async function actualizarNombreProveedorDashboard(codigo: string, nombre: string): Promise<void> {
  if (!supabaseConfigurado) return demoActualizarNombreProveedor(codigo, nombre)

  const aplicarEnCache = (): void => {
    const proveedores = leerCache<Proveedor>(CLAVE_CACHE_PROVEEDORES) ?? demoListarProveedores()
    const idx = proveedores.findIndex((p) => p.codigo === codigo)
    if (idx === -1) return
    proveedores[idx] = { ...proveedores[idx], nombre }
    guardarCache(CLAVE_CACHE_PROVEEDORES, proveedores)
  }

  try {
    const { error } = await supabase.from('proveedores').update({ nombre }).eq('codigo', codigo)
    if (error) throw error
    aplicarEnCache()
  } catch (err) {
    if (!pareceFallaDeRed(err)) throw err
    aplicarEnCache()
  }
}

export async function completarFichaProducto(
  productoId: string,
  cambios: {
    nombreComercial?: string
    precioVentaCol?: number
    precioVentaPan?: number
    notas?: string
    categoria?: string
    linea?: string
    sublinea?: string
    unidadesPorContenedor?: number
  },
): Promise<void> {
  if (!supabaseConfigurado) return demoCompletarFicha(productoId, cambios)

  const aplicarEnCache = (): void => {
    const productos = leerCache<Producto>(CLAVE_CACHE_PRODUCTOS) ?? demoListarProductos()
    const idx = productos.findIndex((p) => p.id === productoId)
    if (idx === -1) return
    productos[idx] = { ...productos[idx], ...cambios }
    guardarCache(CLAVE_CACHE_PRODUCTOS, productos)
  }

  try {
    const { error } = await supabase
      .from('productos')
      .update({
        nombre_comercial: cambios.nombreComercial,
        precio_venta_col: cambios.precioVentaCol,
        precio_venta_pan: cambios.precioVentaPan,
        notas: cambios.notas,
        categoria: cambios.categoria,
        linea: cambios.linea,
        sublinea: cambios.sublinea,
        unidades_contenedor: cambios.unidadesPorContenedor,
      })
      .eq('id', productoId)

    if (error) throw error
    aplicarEnCache()
  } catch (err) {
    if (!pareceFallaDeRed(err)) throw err
    aplicarEnCache()
  }
}

export function suscribirCambiosProductos(alCambiar: () => void) {
  if (!supabaseConfigurado) return demoSuscribir(alCambiar)
  if (!navigator.onLine) return () => {}

  const canal = supabase
    .channel('productos-realtime')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'productos' }, alCambiar)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'telas' }, alCambiar)
    .subscribe()

  return () => {
    supabase.removeChannel(canal)
  }
}
